import { useEffect, useMemo } from 'react';
import {
  buildFindingSourceViewerUrl,
  type FindingViewerFields,
} from '../utils/findingSourceLinks';
import { warmSourceDocument } from '../utils/sourceDocumentCache';

// Runaway guard only. Every finding's source document is warmed on load so
// opening any finding is instant; findings dedup by URL first (a workspace
// citing a handful of regulations warms far below this) and the cache
// throttles the actual fetches. Anything past the guard still loads on open —
// logged, never silent.
const MAX_PREFETCH = 200;

/**
 * Warm every finding's source document into the in-memory cache on load, so
 * opening a finding renders its source instantly (EUR-Lex HTML via srcDoc =
 * zero network; PDFs via a warmed HTTP cache). Unlike lazy <link rel=prefetch>
 * this fetches eagerly at normal priority and is browser-independent — it
 * can't be starved.
 */
export function useFindingSourcePrefetch(
  findings: Record<string, FindingViewerFields> | null | undefined,
): void {
  const uniqueViewerUrls = useMemo(() => {
    if (!findings) return [] as string[];
    const urls = new Set<string>();
    for (const finding of Object.values(findings)) {
      const url = buildFindingSourceViewerUrl(finding);
      if (url) urls.add(url);
    }
    return Array.from(urls);
  }, [findings]);

  // Stable primitive dep: identity-only re-renders of `findings` don't re-warm.
  const prefetchKey = uniqueViewerUrls.join('|');

  useEffect(() => {
    if (uniqueViewerUrls.length === 0) return;

    const toWarm = uniqueViewerUrls.slice(0, MAX_PREFETCH);
    if (uniqueViewerUrls.length > MAX_PREFETCH) {
      // eslint-disable-next-line no-console
      console.info(
        `[finding-prefetch] warming ${MAX_PREFETCH} of ${uniqueViewerUrls.length} ` +
          `source documents; the remainder load on open (still cached).`,
      );
    }
    for (const url of toWarm) warmSourceDocument(url);
    // Warmed documents intentionally persist in the cache for the session —
    // that's the point — so there is nothing to tear down on change/unmount.
    // uniqueViewerUrls is recomputed from findings; prefetchKey captures its
    // value so identity-only re-renders don't re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetchKey]);
}
